/**
 * Shape generation and center calculation logic
 */

import type { Shape, ShapeType } from '../utils/types';
import {
  SHAPE_COLORS,
  SHAPE_MIN_SIZE_PERCENT,
  SHAPE_MAX_SIZE_PERCENT,
} from '../utils/constants';

/**
 * Generates a random shape with position, size, rotation, and color
 */
export function generateShape(
  canvasWidth: number,
  canvasHeight: number
): Shape {
  const type = getRandomShapeType();
  const minDimension = Math.min(canvasWidth, canvasHeight);
  const size = getRandomSize(minDimension);
  const position = getRandomPosition(canvasWidth, canvasHeight, size);
  const rotation = getRandomRotation();
  const color = getRandomColor();

  return {
    type,
    x: position.x,
    y: position.y,
    size,
    rotation,
    color,
  };
}

/**
 * Returns a random shape type
 */
export function getRandomShapeType(): ShapeType {
  const types: ShapeType[] = ['circle', 'triangle', 'square', 'rectangle', 'pentagon'];
  return types[Math.floor(Math.random() * types.length)];
}

/**
 * Generates a random size based on canvas dimensions
 */
export function getRandomSize(minDimension: number): number {
  const minSize = (minDimension * SHAPE_MIN_SIZE_PERCENT) / 100;
  const maxSize = (minDimension * SHAPE_MAX_SIZE_PERCENT) / 100;
  return minSize + Math.random() * (maxSize - minSize);
}

/**
 * Generates a random position ensuring shape stays fully visible
 */
export function getRandomPosition(
  canvasWidth: number,
  canvasHeight: number,
  size: number
): { x: number; y: number } {
  // Add padding to ensure shape doesn't clip edges
  const padding = size / 2;
  const minX = padding;
  const maxX = canvasWidth - padding;
  const minY = padding;
  const maxY = canvasHeight - padding;

  return {
    x: minX + Math.random() * (maxX - minX),
    y: minY + Math.random() * (maxY - minY),
  };
}

/**
 * Generates a random rotation in radians (-π to π)
 */
export function getRandomRotation(): number {
  return (Math.random() - 0.5) * 2 * Math.PI;
}

/**
 * Returns a random color from the shape color palette
 */
export function getRandomColor(): string {
  return SHAPE_COLORS[Math.floor(Math.random() * SHAPE_COLORS.length)];
}

/**
 * Calculates the center point of a shape
 * For all shapes in this game, the center is simply (x, y)
 */
export function getShapeCenter(shape: Shape): { x: number; y: number } {
  return { x: shape.x, y: shape.y };
}

/**
 * Calculates the distance between two points
 */
export function calculateDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Determines if a click is within the shape bounds
 * Uses a simple circular hit detection based on shape size
 */
export function isClickInShape(
  clickX: number,
  clickY: number,
  shape: Shape
): boolean {
  const distance = calculateDistance(clickX, clickY, shape.x, shape.y);
  // Use size as radius for hit detection
  return distance <= shape.size / 2;
}
